// Lightweight client-side tracker for recently visited recovery tools.
// Stored in localStorage as `rt:recent-activity` — capped at 12 entries.

export type ActivityKind =
  | "sounds"
  | "meditations"
  | "breathing"
  | "somatic"
  | "grounding"
  | "journal"
  | "dreams"
  | "library"
  | "therapy"
  | "check-in"
  | "therapists"
  | "sleep"
  | "yoga"
  | "tapping"
  | "affirmations"
  | "other";

export type RecentItem = {
  path: string;
  label: string;
  emoji: string;
  kind: ActivityKind;
  ts: number;
};

const STORAGE_KEY = "rt:recent-activity";
const MAX = 12;

type CatalogEntry = { label: string; emoji: string; kind: ActivityKind };

// Static catalog of paths we want to surface in Recently Used / Continue.
// Anything not listed here is ignored (e.g. /login, /admin, /legal pages).
export const TRACKED_PATHS: Record<string, CatalogEntry> = {
  "/sounds": { label: "Sounds", emoji: "🎧", kind: "sounds" },
  "/meditations": { label: "Meditations", emoji: "🧘", kind: "meditations" },
  "/breathing": { label: "Breathing Exercises", emoji: "🌬️", kind: "breathing" },
  "/somatic": { label: "Somatic Practices", emoji: "💛", kind: "somatic" },
  "/grounding": { label: "Grounding Tools", emoji: "🌿", kind: "grounding" },
  "/journal": { label: "Healing Journal", emoji: "📓", kind: "journal" },
  "/dreams": { label: "Dream Journal", emoji: "🌙", kind: "dreams" },
  "/guided-journal": { label: "Guided Prompts", emoji: "✍️", kind: "journal" },
  "/library": { label: "Learning Resources", emoji: "📚", kind: "library" },
  "/therapy-approaches": { label: "Therapy Approaches", emoji: "🪷", kind: "therapy" },
  "/therapists": { label: "Therapist Directory", emoji: "🤝", kind: "therapists" },
  "/sleep": { label: "Sleep Tools", emoji: "🛌", kind: "sleep" },
  "/yoga": { label: "Movement & Yoga", emoji: "🧘‍♀️", kind: "yoga" },
  "/tapping": { label: "EFT Tapping", emoji: "🤲", kind: "tapping" },
  "/affirmations": { label: "Affirmations", emoji: "✨", kind: "affirmations" },
  "/crisis": { label: "Crisis Resources", emoji: "🆘", kind: "other" },
};

function safeRead(): RecentItem[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (i): i is RecentItem =>
        i && typeof i.path === "string" && typeof i.ts === "number",
    );
  } catch {
    return [];
  }
}

export function recordVisit(path: string) {
  if (typeof window === "undefined") return;
  // Normalise — strip query and trailing slash
  const clean = path.split("?")[0].split("#")[0].replace(/\/$/, "") || "/";
  const entry = TRACKED_PATHS[clean];
  if (!entry) return;
  const existing = safeRead().filter((i) => i.path !== clean);
  const next: RecentItem[] = [
    { path: clean, label: entry.label, emoji: entry.emoji, kind: entry.kind, ts: Date.now() },
    ...existing,
  ].slice(0, MAX);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    window.dispatchEvent(new CustomEvent("rt-recent-activity-change"));
  } catch {
    /* noop */
  }
}

export function getRecent(): RecentItem[] {
  return safeRead();
}

export function getRecentByKinds(kinds: ActivityKind[]): RecentItem[] {
  const set = new Set(kinds);
  return safeRead().filter((i) => set.has(i.kind));
}
